import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Box,
  Divider,
  Badge,
  useMantineColorScheme,
  Select,
  Radio,
  NumberInput,
  Textarea,
  Checkbox,
  Alert,
  Card,
  Loader,
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconUserCheck,
} from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  adminSubscriptionService,
  UserSubscriptionDetails,
} from '../../services/adminSubscriptionService';
import {
  subscriptionPlanService,
  SubscriptionPlanDTO,
} from '../../services/subscriptionPlanService';
import { UserManagement } from '../../services/userManagementService';

interface Props {
  opened: boolean;
  onClose: () => void;
  user: UserManagement | null;
  onSuccess: () => void;
}

type SubscriptionMode = 'regular' | 'custom';
type DurationType = 'days' | 'months' | 'years' | 'exact-date';

const ManageSubscriptionModal: React.FC<Props> = ({
  opened,
  onClose,
  user,
  onSuccess,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlanDTO[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscriptionDetails | null>(null);

  // Form state
  const [mode, setMode] = useState<SubscriptionMode>('custom');
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [durationType, setDurationType] = useState<DurationType>('months');
  const [durationValue, setDurationValue] = useState<number>(1);
  const [exactDate, setExactDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);

  const cardStyle = {
    backgroundColor:
      colorScheme === 'dark'
        ? 'rgba(30, 41, 59, 0.7)'
        : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${
      colorScheme === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(221, 216, 209, 0.8)'
    }`,
  };

  useEffect(() => {
    if (opened && user) {
      loadData();
    }
  }, [opened, user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoadingDetails(true);
      const [plansData, subscriptionData] = await Promise.all([
        subscriptionPlanService.getAllActivePlans(),
        adminSubscriptionService.getUserSubscription(user.id).catch(() => null),
      ]);

      setPlans(plansData);
      setCurrentSubscription(subscriptionData);

      // Pre-select current plan if exists
      if (subscriptionData?.planId) {
        setSelectedPlanId(subscriptionData.planId);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudieron cargar los datos de suscripción',
        color: 'red',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const calculateEndDate = (): Date | null => {
    if (durationType === 'exact-date') {
      return exactDate;
    }

    if (!durationValue || durationValue <= 0) return null;

    const now = new Date();
    const result = new Date(now);

    switch (durationType) {
      case 'days':
        result.setDate(result.getDate() + durationValue);
        break;
      case 'months':
        result.setMonth(result.getMonth() + durationValue);
        break;
      case 'years':
        result.setFullYear(result.getFullYear() + durationValue);
        break;
    }

    return result;
  };

  const getDurationInDays = (): number | null => {
    const endDate = calculateEndDate();
    if (!endDate) return null;

    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleAssignSubscription = async () => {
    if (!user || !selectedPlanId) {
      notifications.show({
        title: 'Error',
        message: 'Por favor selecciona un plan de suscripción',
        color: 'red',
      });
      return;
    }

    if (mode === 'custom') {
      const endDate = calculateEndDate();
      if (!endDate) {
        notifications.show({
          title: 'Error',
          message: 'Por favor especifica la duración de la suscripción',
          color: 'red',
        });
        return;
      }
    }

    try {
      setLoading(true);

      if (mode === 'custom') {
        const endDate = calculateEndDate();
        const durationDays = getDurationInDays();

        await adminSubscriptionService.assignCustomSubscription(user.id, {
          planId: selectedPlanId,
          durationDays: durationDays || undefined,
          customEndDate: endDate?.toISOString(),
          notes: notes || undefined,
          notifyUser,
        });

        notifications.show({
          title: 'Éxito',
          message: 'Suscripción custom asignada correctamente',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        // For regular subscriptions, we would use a different endpoint
        // For now, we'll use custom with standard duration
        notifications.show({
          title: 'Info',
          message: 'Las suscripciones regulares están en desarrollo',
          color: 'blue',
        });
        return;
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error assigning subscription:', error);
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo asignar la suscripción',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExtendSubscription = async () => {
    if (!user || !currentSubscription || !currentSubscription.isCustom) {
      notifications.show({
        title: 'Error',
        message: 'Solo se pueden extender suscripciones custom existentes',
        color: 'red',
      });
      return;
    }

    const additionalDays = getDurationInDays();
    if (!additionalDays || additionalDays <= 0) {
      notifications.show({
        title: 'Error',
        message: 'Por favor especifica los días a extender',
        color: 'red',
      });
      return;
    }

    try {
      setLoading(true);

      await adminSubscriptionService.extendSubscription(user.id, additionalDays);

      notifications.show({
        title: 'Éxito',
        message: `Suscripción extendida ${additionalDays} días`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error extending subscription:', error);
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo extender la suscripción',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !currentSubscription) {
      return;
    }

    if (!window.confirm('¿Estás seguro de que deseas cancelar la suscripción de este usuario?')) {
      return;
    }

    try {
      setLoading(true);

      await adminSubscriptionService.cancelSubscription(user.id);

      notifications.show({
        title: 'Éxito',
        message: 'Suscripción cancelada correctamente',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo cancelar la suscripción',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMode('custom');
    setSelectedPlanId(null);
    setDurationType('months');
    setDurationValue(1);
    setExactDate(null);
    setNotes('');
    setNotifyUser(true);
  };

  const formatDateString = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: es });
    } catch {
      return 'N/A';
    }
  };

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const calculatedEndDate = calculateEndDate();
  const durationInDays = getDurationInDays();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Gestionar Suscripción"
      centered
      size="xl"
      radius="lg"
      styles={{
        content: {
          ...cardStyle,
          padding: '24px',
        },
        header: {
          backgroundColor: 'transparent',
          borderBottom: 'none',
          paddingBottom: '12px',
        },
        title: {
          color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
          fontSize: '1.5rem',
          fontWeight: 600,
        },
      }}
    >
      <Stack gap="lg">
        {/* User Info */}
        {user && (
          <Box>
            <Group gap="xs">
              <IconUserCheck size={18} />
              <Text size="sm" fw={600}>
                Usuario: {user.nombre} {user.apellidos}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </Box>
        )}

        <Divider />

        {/* Current Subscription */}
        {loadingDetails ? (
          <Box ta="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed" mt="xs">
              Cargando detalles...
            </Text>
          </Box>
        ) : currentSubscription ? (
          <Card withBorder radius="md" p="md" style={cardStyle}>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Suscripción Actual
                </Text>
                <Group gap="xs">
                  <Badge
                    color={currentSubscription.isActive ? 'green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {currentSubscription.status}
                  </Badge>
                  {currentSubscription.isCustom && (
                    <Badge color="violet" variant="light" size="sm">
                      Custom
                    </Badge>
                  )}
                </Group>
              </Group>

              <Group grow>
                <Box>
                  <Text size="xs" c="dimmed">
                    Plan
                  </Text>
                  <Text size="sm" fw={500}>
                    {currentSubscription.planName}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Precio
                  </Text>
                  <Text size="sm" fw={500}>
                    ${currentSubscription.planPrice} {currentSubscription.currency}
                  </Text>
                </Box>
              </Group>

              <Group grow>
                <Box>
                  <Text size="xs" c="dimmed">
                    Inicio
                  </Text>
                  <Text size="xs">{formatDateString(currentSubscription.startDate)}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Fin
                  </Text>
                  <Text size="xs">{formatDateString(currentSubscription.endDate)}</Text>
                </Box>
              </Group>

              {currentSubscription.daysRemaining !== undefined && (
                <Alert
                  icon={<IconClock size={16} />}
                  color={currentSubscription.daysRemaining > 7 ? 'blue' : 'orange'}
                  variant="light"
                >
                  <Text size="xs">
                    {currentSubscription.daysRemaining} días restantes
                  </Text>
                </Alert>
              )}

              {currentSubscription.isCustom && currentSubscription.customNotes && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Notas
                  </Text>
                  <Text size="xs">{currentSubscription.customNotes}</Text>
                </Box>
              )}

              {currentSubscription.isCustom && currentSubscription.assignedByAdminName && (
                <Text size="xs" c="dimmed">
                  Asignado por: {currentSubscription.assignedByAdminName}
                </Text>
              )}
            </Stack>
          </Card>
        ) : (
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">Este usuario no tiene una suscripción activa</Text>
          </Alert>
        )}

        <Divider />

        {/* Subscription Mode */}
        <Box>
          <Text size="sm" fw={600} mb="xs">
            Tipo de Suscripción
          </Text>
          <Radio.Group value={mode} onChange={(value) => setMode(value as SubscriptionMode)}>
            <Stack gap="xs">
              <Radio
                value="custom"
                label="Suscripción Custom (Recomendado)"
                description="Asignar suscripción personalizada con duración específica"
              />
              <Radio
                value="regular"
                label="Suscripción Regular"
                description="Vincular con sistema de pagos (próximamente)"
                disabled
              />
            </Stack>
          </Radio.Group>
        </Box>

        {/* Plan Selection */}
        <Select
          label="Plan de Suscripción"
          placeholder="Selecciona un plan"
          data={plans.map((plan) => ({
            value: plan.id.toString(),
            label: `${plan.name} - $${plan.price} ${plan.currency}/${plan.billingInterval}`,
          }))}
          value={selectedPlanId?.toString() || null}
          onChange={(value) => setSelectedPlanId(value ? parseInt(value) : null)}
          searchable
          required
        />

        {/* Duration Configuration (Custom mode only) */}
        {mode === 'custom' && (
          <Box>
            <Text size="sm" fw={600} mb="xs">
              Duración
            </Text>
            <Radio.Group
              value={durationType}
              onChange={(value) => setDurationType(value as DurationType)}
            >
              <Stack gap="xs">
                <Radio value="days" label="Días" />
                <Radio value="months" label="Meses" />
                <Radio value="years" label="Años" />
                <Radio value="exact-date" label="Fecha exacta" />
              </Stack>
            </Radio.Group>

            {durationType !== 'exact-date' ? (
              <NumberInput
                mt="sm"
                placeholder={`Cantidad de ${durationType}`}
                value={durationValue}
                onChange={(value) => setDurationValue(Number(value))}
                min={1}
                max={durationType === 'days' ? 365 : durationType === 'months' ? 24 : 10}
              />
            ) : (
              <DateInput
                mt="sm"
                placeholder="Selecciona fecha de expiración"
                value={exactDate}
                onChange={setExactDate}
                minDate={new Date()}
                locale="es"
                leftSection={<IconCalendar size={16} />}
              />
            )}
          </Box>
        )}

        {/* Notes */}
        {mode === 'custom' && (
          <Textarea
            label="Notas (opcional)"
            placeholder="Razón de la asignación, detalles del acuerdo, etc."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={2}
            maxRows={4}
          />
        )}

        {/* Notify User */}
        {mode === 'custom' && (
          <Checkbox
            label="Notificar al usuario por correo"
            checked={notifyUser}
            onChange={(e) => setNotifyUser(e.currentTarget.checked)}
          />
        )}

        {/* Summary */}
        {selectedPlan && calculatedEndDate && mode === 'custom' && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="teal"
            variant="light"
            title="Resumen"
          >
            <Stack gap="xs">
              <Text size="xs">
                Plan: <strong>{selectedPlan.name}</strong>
              </Text>
              <Text size="xs">
                Precio: <strong>${selectedPlan.price} {selectedPlan.currency}</strong>
              </Text>
              <Text size="xs">
                Duración: <strong>{durationInDays} días</strong>
              </Text>
              <Text size="xs">
                Expira: <strong>{format(calculatedEndDate, 'dd/MM/yyyy', { locale: es })}</strong>
              </Text>
            </Stack>
          </Alert>
        )}

        {/* Warning for existing subscriptions */}
        {currentSubscription && currentSubscription.isActive && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="xs">
              Al asignar una nueva suscripción, se cancelará la suscripción actual del usuario.
            </Text>
          </Alert>
        )}

        {/* Action Buttons */}
        <Group justify="space-between" mt="md">
          <Group>
            {currentSubscription && currentSubscription.isActive && (
              <Button
                variant="light"
                color="red"
                onClick={handleCancelSubscription}
                loading={loading}
                leftSection={<IconX size={16} />}
              >
                Cancelar Suscripción
              </Button>
            )}
          </Group>

          <Group>
            <Button variant="subtle" onClick={onClose} disabled={loading}>
              Cerrar
            </Button>

            {currentSubscription?.isCustom && (
              <Button
                variant="light"
                onClick={handleExtendSubscription}
                loading={loading}
                leftSection={<IconClock size={16} />}
              >
                Extender
              </Button>
            )}

            <Button
              onClick={handleAssignSubscription}
              loading={loading}
              disabled={!selectedPlanId || (mode === 'custom' && !calculatedEndDate)}
              leftSection={<IconCheck size={16} />}
            >
              Asignar Suscripción
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ManageSubscriptionModal;
